import React, { useState } from 'react'

import FeatureNotEnabled from '../../components/FeatureNotEnabled'
import { Card } from '../../components/UI'
import {
    Globe, Shield, HardDrive, RefreshCw, Activity,
    Settings2, Info, CheckCircle2, XCircle
} from 'lucide-react'

type TabType = 'status' | 'cache' | 'intercept' | 'docs'

export default function ServiceWorkerPage() {
    const [tab, setTab] = useState<TabType>('status')
    const [swActive, setSwActive] = useState(true)

    const cacheEntries = [
        { url: '/index.html', size: '4.2 KB', strategy: 'network-first' },
        { url: '/assets/index-*.js', size: '185 KB', strategy: 'cache-first' },
        { url: '/assets/index-*.css', size: '12 KB', strategy: 'cache-first' },
        { url: '/vendor_core.wasm', size: '45 KB', strategy: 'cache-first' },
        { url: '/api/validate_license', size: '1.2 KB', strategy: 'stale-while-revalidate' },
    ]

    const rules = [
        { pattern: '/api/*', action: 'Proxy + Cache', desc: 'Cachea respuestas válidas, sirve cache offline' },
        { pattern: '*.wasm', action: 'Cache-First', desc: 'Cache permanente con hash de integridad' },
        { pattern: '/assets/*', action: 'Cache-First', desc: 'Assets hasheados, cache permanente' },
        { pattern: '/realtime/*', action: 'Pass-Through', desc: 'WebSockets sin interceptar' },
        { pattern: '*.map', action: 'Block', desc: 'Source maps bloqueados en producción' },
    ]

    const tabs = [
        { id: 'status' as const, label: 'Estado', icon: <Activity size={16} /> },
        { id: 'cache' as const, label: 'Cache', icon: <HardDrive size={16} /> },
        { id: 'intercept' as const, label: 'Interceptor', icon: <Shield size={16} /> },
        { id: 'docs' as const, label: 'Docs', icon: <Info size={16} /> },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <Globe className="text-neon-blue" /> Service Worker
                </h1>
                <p className="text-gray-500 text-sm">Interceptor de red, cache inteligente y protección offline</p>
            </div>

            <FeatureNotEnabled moduleName="Service Worker" />

            <Card className={`mb-6 ${swActive ? 'bg-neon-green/5 border-neon-green/20' : 'bg-red-500/5 border-red-500/20'} border`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {swActive ? <CheckCircle2 size={24} className="text-neon-green" /> : <XCircle size={24} className="text-red-400" />}
                        <div>
                            <span className={`font-bold text-sm ${swActive ? 'text-neon-green' : 'text-red-400'}`}>
                                {swActive ? 'Service Worker Activo' : 'Service Worker Inactivo'}
                            </span>
                            <p className="text-gray-500 text-xs mt-0.5">Scope: / — sw.js v4.3.0</p>
                        </div>
                    </div>
                    <button onClick={() => setSwActive(!swActive)} className="px-3 py-2 bg-white/5 text-gray-400 border border-white/10 rounded-lg text-xs hover:bg-white/10 transition-all">
                        <RefreshCw size={14} className="inline mr-1" /> Toggle
                    </button>
                </div>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                    { label: 'Cache Size', value: '248 KB', color: 'text-blue-400' },
                    { label: 'Entries', value: '6', color: 'text-neon-green' },
                    { label: 'Hit Rate', value: '94%', color: 'text-purple-400' },
                    { label: 'Intercepted', value: '1,247', color: 'text-yellow-400' },
                ].map((s, i) => (
                    <Card key={i} className="!p-4 text-center">
                        <div className="text-xl font-bold font-mono text-white">{s.value}</div>
                        <div className={`text-[10px] uppercase tracking-wider mt-1 ${s.color}`}>{s.label}</div>
                    </Card>
                ))}
            </div>

            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'status' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4">Funcionalidades</h2>
                    <div className="space-y-2">
                        {[
                            { f: 'Offline Fallback', on: true, d: 'Sirve cache si la red falla' },
                            { f: 'API Proxy', on: true, d: 'Intercepta/cachea Supabase RPCs' },
                            { f: 'Source Map Block', on: true, d: 'Bloquea .map en producción' },
                            { f: 'Precache Assets', on: true, d: 'JS/CSS/Wasm en install' },
                            { f: 'Background Sync', on: false, d: 'Sync requests fallidos' },
                        ].map(item => (
                            <div key={item.f} className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
                                <div className="flex items-center gap-3">
                                    {item.on ? <CheckCircle2 size={14} className="text-neon-green" /> : <XCircle size={14} className="text-gray-600" />}
                                    <div>
                                        <span className="text-white text-sm">{item.f}</span>
                                        <p className="text-gray-600 text-xs">{item.d}</p>
                                    </div>
                                </div>
                                <span className={`text-[10px] font-mono ${item.on ? 'text-neon-green' : 'text-gray-600'}`}>{item.on ? 'ON' : 'OFF'}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {tab === 'cache' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                        <HardDrive size={16} className="text-neon-blue" /> Cache Storage
                    </h2>
                    <div className="space-y-2">
                        {cacheEntries.map((e, i) => (
                            <div key={i} className="flex items-center justify-between p-3 bg-black/20 rounded-lg text-sm">
                                <code className="text-neon-blue text-xs font-mono">{e.url}</code>
                                <div className="flex items-center gap-4">
                                    <span className="text-gray-400 text-xs">{e.size}</span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-white/5 text-gray-400">{e.strategy}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {tab === 'intercept' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                        <Shield size={16} className="text-neon-blue" /> Reglas de Interceptación
                    </h2>
                    <div className="space-y-3">
                        {rules.map((r, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 bg-black/20 rounded-lg">
                                <code className="text-neon-blue text-xs font-mono bg-neon-blue/5 px-2 py-0.5 rounded w-24 text-center shrink-0">{r.pattern}</code>
                                <div className="flex-1">
                                    <span className="text-white text-sm font-medium">{r.action}</span>
                                    <p className="text-gray-600 text-xs mt-0.5">{r.desc}</p>
                                </div>
                                <span className="text-neon-green text-[10px] font-mono">● ON</span>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {tab === 'docs' && (
                <div className="space-y-4">
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-4">¿Por qué Service Workers?</h2>
                        <div className="text-gray-400 text-sm space-y-2 leading-relaxed">
                            <p>Actúan como <strong className="text-white">proxy transparente</strong> entre la app y la red:</p>
                            <ul className="space-y-1 ml-4">
                                <li>• Funcionamiento offline con cache inteligente</li>
                                <li>• Bloqueo de source maps en producción</li>
                                <li>• Pre-cache de assets críticos</li>
                                <li>• Verificación de integridad de recursos</li>
                            </ul>
                        </div>
                    </Card>
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-4">Estrategias de Cache</h2>
                        <div className="space-y-2">
                            {[
                                { n: 'Cache-First', d: 'Cache → fetch si no existe', u: 'Assets, Wasm' },
                                { n: 'Network-First', d: 'Red → cache si falla', u: 'HTML, auth' },
                                { n: 'Stale-While-Revalidate', d: 'Cache inmediato + update background', u: 'API licencias' },
                                { n: 'Network-Only', d: 'Siempre red, sin cache', u: 'Comandos, WS' },
                            ].map(s => (
                                <div key={s.n} className="p-3 bg-black/20 rounded-lg">
                                    <code className="text-neon-blue text-xs font-mono">{s.n}</code>
                                    <p className="text-gray-400 text-xs mt-1">{s.d} — <span className="text-gray-600">Uso: {s.u}</span></p>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )}
        </div>
    )
}
